/**
 * How the meeting treats screen-share-only peers — someone in the room as a
 * screen, from the dashboard's Screen Share (contract §8 in
 * docs/screen-share-only-join-checkpoint.md).
 *
 * They are listed once in the People panel and counted in its headcount, and
 * their screen is on the stage like anyone's. Everything else that is about
 * PEOPLE leaves them out: no tile in any layout, no spotlight or pin, no
 * active-speaker highlight, no Picture-in-Picture, no local-recording tile, no
 * private chat, no breakout room, no vote, no "mute all".
 *
 * Every decision here goes through `isShareOnlyPeer` (services/share-only),
 * the one predicate: on a roster row, its id with the Presence flag the server
 * sets when it tracks the session.
 */
import { isShareOnlyPeer } from './share-only'

/** Anything that names a peer, and may carry its Presence flag. */
export type ShareMarked = { id: string; sfuPeerId?: string; shareOnly?: boolean }

/** This roster row is a screen-share session. */
export function isShareOnlyRow(row: ShareMarked): boolean {
  return isShareOnlyPeer(row.sfuPeerId ?? row.id, { share_only: row.shareOnly })
}

/**
 * People only — every row but share sessions'.
 *
 * The same array when there were none, so a memo built on the result holds
 * exactly as it did before share sessions existed.
 */
export function withoutShareOnly<T extends ShareMarked>(rows: T[]): T[] {
  return rows.some(isShareOnlyRow) ? rows.filter((row) => !isShareOnlyRow(row)) : rows
}

/** How many of `rows` are share sessions. */
export function countShareOnly(rows: readonly ShareMarked[]): number {
  let n = 0
  for (const row of rows) if (isShareOnlyRow(row)) n += 1
  return n
}

/**
 * People in the room, you included — what the layout choice, polls, "pin for
 * all" and the "you're the only one here" popover go by. Not share sessions,
 * and not anyone this moderator just removed (hidden until they have gone).
 */
export function peopleCount(remotes: readonly ShareMarked[], removed: ReadonlySet<string>): number {
  let n = 1
  for (const row of remotes) if (!removed.has(row.id) && !isShareOnlyRow(row)) n += 1
  return n
}

/**
 * Who can be put in a breakout room: people — not their extra cameras, and
 * never a share session (decision 17).
 */
export function breakoutCandidates<T extends ShareMarked & { sourceKind?: string }>(rows: T[]): T[] {
  return withoutShareOnly(rows.filter((row) => row.sourceKind !== 'additional'))
}

/**
 * The speaker Picture-in-Picture shows beside you: the active speaker if they
 * are someone else with a picture of their own — not an extra camera, not a
 * share session.
 */
export function pipSpeaker<T extends ShareMarked & { isLocal?: boolean; sourceKind?: string }>(
  rows: readonly T[],
  activeSpeakerId: string | null | undefined,
): T | undefined {
  if (!activeSpeakerId) return undefined
  return rows.find(
    (row) => row.id === activeSpeakerId && !row.isLocal && row.sourceKind !== 'additional' && !isShareOnlyRow(row),
  )
}

/**
 * The loudest speaker the meeting may highlight, from the SFU's ranked list.
 *
 * The SFU's active-speaker observer hears every audio producer, a share
 * page's tab sound included — and a share session has no tile to promote,
 * spotlight or show in Picture-in-Picture. It is skipped, so the person
 * talking over it still gets the stage.
 */
export function speakerCandidate(ranked: readonly string[]): string | null {
  for (const id of ranked) if (!isShareOnlyPeer(id)) return id
  return null
}

/**
 * Share-only askers whose card should come down: they were in the room and
 * now are not.
 *
 * Only share pages: one asks again after its own rejoin (it was told to), so a
 * card taken down for a dropped connection comes back with it. A person in
 * the meeting asks again only when a moderator arrives, so their card stays
 * up as it always has. An asker never seen in the room keeps theirs too: on a
 * multi-node cluster the ask can arrive before their Presence does.
 */
export function departedShareAskers(
  before: ReadonlySet<string>,
  now: ReadonlySet<string>,
  requests: ReadonlyArray<{ peerId: string; shareOnly?: boolean }>,
): string[] {
  return requests
    .filter((r) => isShareOnlyPeer(r.peerId, { share_only: r.shareOnly }))
    .filter((r) => before.has(r.peerId) && !now.has(r.peerId))
    .map((r) => r.peerId)
}

/** A share page a moderator removed from the meeting, for the hosts' "Allow back" list. */
export type BlockedShare = { id: string; name: string | null }

/**
 * `share_blocked` from `roles:updated` (task 5): `[{id, name}]`, name null
 * when the page had already gone. Anything malformed, or not a share id, is
 * dropped — Allow back can only ever be aimed at a share page.
 */
export function parseShareBlocked(raw: unknown): BlockedShare[] {
  if (!Array.isArray(raw)) return []
  const out: BlockedShare[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue
    const { id, name } = entry as { id?: unknown; name?: unknown }
    if (typeof id !== 'string' || !isShareOnlyPeer(id)) continue
    out.push({ id, name: typeof name === 'string' && name.trim() ? name : null })
  }
  return out
}

/**
 * The words a moderator sees for a presenter request: who asked, and — for a
 * share page, whose name is whatever was typed on it — that it is a screen
 * share, and the account's email (decision 6).
 */
export function presenterRequestWho(
  request: { name: string; shareOnly?: boolean; email?: string | null },
  words: { unknown: string; tag: string },
): { who: string; email: string | null } {
  const name = request.name || words.unknown
  if (!request.shareOnly) return { who: name, email: null }
  return { who: `${name} (${words.tag})`, email: request.email || null }
}

/** A waiting-room row is a share page knocking: `mode: "share"`, or its id (contract §5, §1). */
export function isShareLobbyRow(row: { mode?: string | null; guestId?: string }): boolean {
  return row.mode === 'share' || isShareOnlyPeer(row.guestId)
}

/** The same for a waiting-room row. */
export function lobbyRowWho(
  row: { name: string; mode?: string | null; guestId?: string; email?: string | null },
  words: { unknown: string; tag: string },
): { who: string; shareOnly: boolean; email: string | null } {
  const name = row.name || words.unknown
  if (!isShareLobbyRow(row)) return { who: name, shareOnly: false, email: null }
  return { who: `${name} (${words.tag})`, shareOnly: true, email: row.email || null }
}
